import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const MOBILE_BREAKPOINT = 750;
const TABLET_BREAKPOINT = 990;

/**
 * Icon + text carousel combining hero slider and simple slider behaviors.
 *
 * @typedef {Object} IconTextSliderRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} viewport
 * @property {HTMLElement} iconBar
 * @property {HTMLElement} [indicator]
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLElement[]} slides
 * @property {HTMLElement[]} icons
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLElement} [liveRegion]
 *
 * @extends {Component<IconTextSliderRefs>}
 */
export class IconTextSliderComponent extends Component {
  requiredRefs = ['track', 'viewport'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {boolean} */
  #isTransitioning = false;

  /** @type {number | undefined} */
  #autoScrollTimer;

  /** @type {boolean} - Whether the visitor has explicitly paused autoplay (WCAG 2.2.2) */
  #userPaused = false;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #touchEndX = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#activeIndex = this.#getInitialIndex();
    this.#syncSlideState({ animate: false });
    this.#updateIndicator();

    this.#bindViewportEvents();
    this.#startAutoScroll();

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#updateIndicator();
        this.#positionFlankArrows();
      });
      this.#resizeObserver.observe(this);
    }

    this.#positionFlankArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoScroll();
    this.#resizeObserver?.disconnect();
    this.#unbindViewportEvents();
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /**
   * @param {Event} event
   */
  handlePreviousClick(event) {
    event.preventDefault();
    this.#goToIndex(this.#activeIndex - 1);
  }

  /**
   * @param {Event} event
   */
  handleNextClick(event) {
    event.preventDefault();
    this.#goToIndex(this.#activeIndex + 1);
  }

  /**
   * Resolves the element with `on:click` from a delegated click.
   * Horizon proxies `event.target` (not `currentTarget`) because listeners use capture on document.
   *
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getClickTarget(event) {
    const target = event.target;

    return target instanceof HTMLElement ? target : null;
  }

  /**
   * @param {Event} event
   */
  handleIconClick(event) {
    event.preventDefault();

    const target = this.#getClickTarget(event);

    if (!target) return;

    const index = Number.parseInt(target.dataset.slideIndex ?? '', 10);

    if (Number.isNaN(index)) return;

    this.#goToIndex(index);
  }

  /**
   * @param {Event} event
   */
  handleDotClick(event) {
    event.preventDefault();

    const target = this.#getClickTarget(event);

    if (!target) return;

    const index = Number.parseInt(target.dataset.slideIndex ?? '', 10);

    if (Number.isNaN(index)) return;

    this.#goToIndex(index);
  }

  /**
   * Visitor explicitly pauses autoplay.
   * @param {Event} event
   */
  handlePauseClick(event) {
    event.preventDefault();
    this.#userPaused = true;
    this.setAttribute('data-user-paused', '');
    this.#stopAutoScroll();
  }

  /**
   * Visitor explicitly resumes autoplay.
   * @param {Event} event
   */
  handlePlayClick(event) {
    event.preventDefault();
    this.#userPaused = false;
    this.removeAttribute('data-user-paused');
    this.#startAutoScroll();
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goToIndex(this.#activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goToIndex(this.#activeIndex + 1);
    }
  }

  #bindViewportEvents() {
    const { viewport } = this.refs;

    viewport.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    viewport.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
    viewport.addEventListener('touchend', this.#handleTouchEnd);
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('focusin', this.#handleMouseEnter);
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  #unbindViewportEvents() {
    const { viewport } = this.refs;

    viewport.removeEventListener('touchstart', this.#handleTouchStart);
    viewport.removeEventListener('touchmove', this.#handleTouchMove);
    viewport.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('focusin', this.#handleMouseEnter);
    this.removeEventListener('focusout', this.#handleFocusOut);
  }

  #handleMouseEnter = () => {
    this.setAttribute('data-paused', '');
    this.#stopAutoScroll();
  };

  #handleMouseLeave = () => {
    this.removeAttribute('data-paused');
    this.#startAutoScroll();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.removeAttribute('data-paused');
    this.#startAutoScroll();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (this.dataset.enableSwipe !== 'true') return;

    this.#touchStartX = event.targetTouches[0]?.clientX ?? 0;
    this.#touchEndX = this.#touchStartX;
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.dataset.enableSwipe !== 'true') return;

    this.#touchEndX = event.targetTouches[0]?.clientX ?? 0;
  };

  #handleTouchEnd = () => {
    if (this.dataset.enableSwipe !== 'true') return;

    const minSwipeDistance = Number.parseInt(this.dataset.swipeDistance ?? '50', 10);
    const distance = this.#touchStartX - this.#touchEndX;

    if (distance > minSwipeDistance) {
      this.#goToIndex(this.#activeIndex + 1);
    } else if (distance < -minSwipeDistance) {
      this.#goToIndex(this.#activeIndex - 1);
    }

    this.#touchStartX = 0;
    this.#touchEndX = 0;
  };

  #startAutoScroll() {
    this.#stopAutoScroll();

    if (this.dataset.autoScroll !== 'true') return;

    // Respect an explicit visitor pause; hover/focus resume must not override it.
    if (this.#userPaused) return;

    const interval = Number.parseInt(this.dataset.autoScrollInterval ?? '4000', 10);

    this.#autoScrollTimer = window.setInterval(() => {
      if (this.hasAttribute('data-paused')) return;

      this.#goToIndex(this.#activeIndex + 1);
    }, interval);
  }

  #stopAutoScroll() {
    if (this.#autoScrollTimer) {
      window.clearInterval(this.#autoScrollTimer);
      this.#autoScrollTimer = undefined;
    }
  }

  /**
   * @returns {number}
   */
  #getSlideCount() {
    return this.refs.slides?.length ?? 0;
  }

  /**
   * @returns {number}
   */
  #getInitialIndex() {
    const initial = Number.parseInt(this.dataset.initialSlide ?? '0', 10);

    if (Number.isNaN(initial)) return 0;

    return Math.max(0, Math.min(initial, this.#getSlideCount() - 1));
  }

  /**
   * @param {number} index
   */
  #normalizeIndex(index) {
    const count = this.#getSlideCount();

    if (count <= 0) return 0;

    if (index < 0) return count - 1;

    if (index >= count) return 0;

    return index;
  }

  /**
   * @param {number} index
   */
  #goToIndex(index) {
    const count = this.#getSlideCount();

    if (count < 1 || this.#isTransitioning) return;

    if (count === 1) return;

    const nextIndex = this.#normalizeIndex(index);

    if (nextIndex === this.#activeIndex) return;

    this.#isTransitioning = true;
    this.#activeIndex = nextIndex;
    this.#syncSlideState({ animate: true });

    const duration = prefersReducedMotion()
      ? 0
      : Number.parseInt(this.dataset.slideDuration ?? '500', 10);

    window.setTimeout(() => {
      this.#isTransitioning = false;
      this.#positionFlankArrows();
    }, duration);
  }

  /**
   * @param {{ animate: boolean }} options
   */
  #syncSlideState({ animate }) {
    const { track, slides, icons, dots, liveRegion } = this.refs;
    const count = this.#getSlideCount();

    if (count === 0) return;

    track.style.setProperty('--active-index', String(this.#activeIndex));

    if (!animate || prefersReducedMotion()) {
      track.style.setProperty('--slide-duration', '0ms');
    } else {
      track.style.setProperty('--slide-duration', `${this.dataset.slideDuration ?? 500}ms`);
    }

    for (const [index, slide] of slides.entries()) {
      const isActive = index === this.#activeIndex;
      slide.setAttribute('data-active', isActive ? 'true' : 'false');
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, icon] of (icons ?? []).entries()) {
      icon.toggleAttribute('data-active', index === this.#activeIndex);
      icon.setAttribute('aria-current', index === this.#activeIndex ? 'true' : 'false');
    }

    for (const [index, dot] of (dots ?? []).entries()) {
      if (index === this.#activeIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }

    this.dataset.activeSlide = String(this.#activeIndex);

    if (liveRegion) {
      const activeSlide = slides[this.#activeIndex];
      const label = activeSlide?.dataset.slideLabel ?? `Slide ${this.#activeIndex + 1} of ${count}`;
      liveRegion.textContent = label;
    }

    this.#updateIndicator();
    this.#scheduleFlankArrowPosition();
  }

  #scheduleFlankArrowPosition() {
    if (this.dataset.arrowPosition !== 'flank_text') return;

    requestAnimationFrame(() => {
      this.#positionFlankArrows();
    });
  }

  #positionFlankArrows() {
    if (this.dataset.arrowPosition !== 'flank_text') return;

    const { previous, next, slides } = this.refs;

    if (!(previous instanceof HTMLElement) || !(next instanceof HTMLElement) || !slides?.length) return;

    const controls = this.querySelector('.icon-text-slider__controls--flank-fixed');

    if (!(controls instanceof HTMLElement)) return;

    const activeSlide = slides[this.#activeIndex];

    if (!(activeSlide instanceof HTMLElement)) return;

    const slideInner = activeSlide.querySelector('.icon-text-slider__slide-inner');

    if (!(slideInner instanceof HTMLElement)) return;

    const heading = activeSlide.querySelector('.icon-text-slider__slide-heading');
    const content = activeSlide.querySelector('.icon-text-slider__slide-content');

    const controlsRect = controls.getBoundingClientRect();
    const innerRect = slideInner.getBoundingClientRect();

    let textTop = innerRect.top;
    let textBottom = innerRect.bottom;

    const textElements = [heading, content].filter((element) => element instanceof HTMLElement);

    if (textElements.length > 0) {
      textTop = Infinity;
      textBottom = -Infinity;

      for (const element of textElements) {
        const rect = element.getBoundingClientRect();
        textTop = Math.min(textTop, rect.top);
        textBottom = Math.max(textBottom, rect.bottom);
      }
    }

    const textCenterY = (textTop + textBottom) / 2;
    const styles = getComputedStyle(this);
    const arrowOffset = Number.parseFloat(styles.getPropertyValue('--its-arrow-offset')) || 16;
    const arrowHeight = previous.offsetHeight || 44;
    const top = textCenterY - controlsRect.top - arrowHeight / 2;
    const prevWidth = previous.offsetWidth;

    previous.style.top = `${top}px`;
    previous.style.left = `${innerRect.left - controlsRect.left - arrowOffset - prevWidth}px`;
    previous.style.right = 'auto';
    previous.style.transform = 'none';

    next.style.top = `${top}px`;
    next.style.left = `${innerRect.right - controlsRect.left + arrowOffset}px`;
    next.style.right = 'auto';
    next.style.transform = 'none';
  }

  #updateIndicator() {
    const { indicator, icons, iconBar } = this.refs;

    if (!indicator || !icons?.length || !iconBar) return;

    const activeIcon = icons[this.#activeIndex];

    if (!activeIcon) return;

    const barRect = iconBar.getBoundingClientRect();
    const iconRect = activeIcon.getBoundingClientRect();

    indicator.style.width = `${iconRect.width}px`;
    indicator.style.transform = `translateX(${iconRect.left - barRect.left}px)`;
  }

  /**
   * @returns {number}
   */
  getMaxIconsPerRow() {
    const width = window.innerWidth;

    if (width < MOBILE_BREAKPOINT) {
      return Number.parseInt(this.dataset.maxIconsMobile ?? '3', 10);
    }

    if (width < TABLET_BREAKPOINT) {
      return Number.parseInt(this.dataset.maxIconsTablet ?? '4', 10);
    }

    return Number.parseInt(this.dataset.maxIconsDesktop ?? '6', 10);
  }
}

if (!customElements.get('icon-text-slider-component')) {
  customElements.define('icon-text-slider-component', IconTextSliderComponent);
}
